import { ReactElement, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import Button from '@Hotels-at-Home/hah-fe-core/dist/components/Button';
import Link from '@Hotels-at-Home/hah-fe-core/dist/components/Link';
import classnames from 'classnames';
import { Swiper as ReactSwiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import useSwiperRef from '~/ui/components/common/Swiper/useSwiperRef';
import { Mousewheel, Pagination, Navigation } from 'swiper/modules';
import ConditionalWrapper from '@Hotels-at-Home/hah-fe-core/dist/components/ConditionalWrapper';
import { useWindowSize } from '@Hotels-at-Home/hah-fe-core/dist/hooks';

interface IBathItem {
  id: string;
  title: string;
  link: string;
  image: string;
  width: number;
  height: number;
  dark?: boolean;
  doubleSpace?: boolean;
}

const BathSection = (): ReactElement => {
  const { t } = useTranslation('', { keyPrefix: '$pages.$home.$bathSection' });
  const { t: tButton } = useTranslation('', { keyPrefix: '$common.$buttons' });
  const { t: tLink } = useTranslation('', { keyPrefix: '$local.$homePageLinks.$bath' });
  const [nextEl, nextElRef] = useSwiperRef();
  const [prevEl, prevElRef] = useSwiperRef();
  const swiperRef = useRef<SwiperType | null>(null);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const windowSize = useWindowSize();

  const desktopItems: IBathItem[] = [
    {
      id: 'robes',
      title: t('robesTitle'),
      link: tLink('robes'),
      image: '/images/homePage/bathSection/robes.webp',
      width: 1036,
      height: 775,
      dark: true,
      doubleSpace: true,
    },
    {
      id: 'slippers',
      title: t('slippersTitle'),
      link: tLink('slippers'),
      image: '/images/homePage/bathSection/slippers.webp',
      width: 468,
      height: 541,
    },
    {
      id: 'momo-shower-set',
      title: t('momoShowerSetTitle'),
      link: tLink('momoShowerSet'),
      image: '/images/homePage/bathSection/momo-shower-set.webp',
      width: 468,
      height: 541,
    },
    {
      id: 'bath-mat',
      title: t('bathMatTitle'),
      link: tLink('bathMat'),
      image: '/images/homePage/bathSection/bath-mat.webp',
      width: 508,
      height: 682,
      dark: true,
    },
    {
      id: 'body-cream',
      title: t('bodyCreamTitle'),
      link: tLink('bodyCream'),
      image: '/images/homePage/bathSection/body-cream.webp',
      width: 468,
      height: 541,
    },
    {
      id: 'cleansing-cream',
      title: t('cleansingCreamTitle'),
      link: tLink('cleansingCream'),
      image: '/images/homePage/bathSection/cleansing-cream.webp',
      width: 468,
      height: 541,
    },
    {
      id: 'towels',
      title: t('towelsTitle'),
      link: tLink('towels'),
      image: '/images/homePage/bathSection/towels.webp',
      width: 1036,
      height: 775,
      dark: true,
      doubleSpace: true,
    },
  ];
  const mobileItems: IBathItem[] = [
    {
      id: 'robes',
      title: t('robesTitle'),
      link: tLink('robes'),
      image: '/images/homePage/bathSection/robes-mob.webp',
      width: 579,
      height: 726,
      dark: true,
    },
    {
      id: 'bath-and-body',
      title: t('bathAndBodyTitle'),
      link: tLink('bathAndBody'),
      image: '/images/homePage/bathSection/bath-body-mob.webp',
      width: 579,
      height: 726,
      dark: true,
    },
    {
      id: 'towels',
      title: t('towelsTitle'),
      link: tLink('towels'),
      image: '/images/homePage/bathSection/towels-mob.webp',
      width: 579,
      height: 726,
      dark: true,
    },
  ];
  const items = windowSize.width < 660 ? mobileItems : desktopItems;

  return (
    <section className="base-section bath-section hp-section">
      <div className="content-section">
        <h2>{t('title')}</h2>
        <p className="section-description">{t('description')}</p>
      </div>
      <div className="bath-grid">
        <div className="bath-mobile-controls">
          {mobileItems.map((item, index) => (
            <Button
              key={item.id}
              variant="txt"
              ariaLabel={item.title}
              className={classnames({
                active: activeSlideIndex === index,
              })}
              onClick={() => {
                if (swiperRef.current) {
                  swiperRef.current.slideTo(index);
                }
              }}
            >
              {item.title}
            </Button>
          ))}
        </div>
        <ConditionalWrapper
          condition={windowSize.width < 660}
          wrapper={
            <ReactSwiper
              modules={[Mousewheel, Pagination, Navigation]}
              navigation={{
                prevEl,
                nextEl,
              }}
              grabCursor
              slidesPerView={1}
              spaceBetween={75}
              onSwiper={swiper => {
                swiperRef.current = swiper;
              }}
              onSlideChange={swiper => {
                setActiveSlideIndex(swiper.activeIndex);
              }}
            />
          }
        >
          {items.map(item => (
            <SwiperSlide key={item.id}>
              <Link
                key={item.id}
                href={item.link}
                className={classnames('bath-item', {
                  dark: item.dark,
                  'double-space': item.doubleSpace,
                })}
              >
                <div className="bath-item-image">
                  <img
                    src={item.image}
                    alt={item.title}
                    width={item.width}
                    height={item.height}
                    loading="lazy"
                    decoding="async"
                    fetchPriority="low"
                  />
                </div>
                <div className="bath-item-text">
                  <h3>{item.title}</h3>
                  <Button variant={!item.dark ? 'black' : 'main'} ariaLabel={item.title}>
                    {tButton('shopNow')}
                  </Button>
                </div>
              </Link>
            </SwiperSlide>
          ))}
        </ConditionalWrapper>
      </div>
    </section>
  );
};

export default BathSection;
